import React, { FC } from 'react'
import ActionGroup from '@pixel/core/ActionGroup'
import Box from '@pixel/core/Box'
import type { ButtonProps } from '@pixel/core/Button'
import DataTable from '@pixel/core/DataTable'
import Link from '@pixel/core/Link'
import Paper from '@pixel/core/Paper'
import Typography from '@pixel/core/Typography'
import { styled } from '@pixel/core/styles'
import LocalizedTypography from '../../components/LocalizedTypography'
import { FieldListSearchResult, FieldListStatusType } from '../types/FieldList'
import { LocalizedText } from '../../contexts/Localization/LocalizationTypes'
import { useMultipleLocalizedText } from '../../contexts/Localization/localizationHooks'
import { FieldListsListBaseNodeContext } from './FieldListsListBase'

const fieldListStatusTranslationMap = {
    active: LocalizedText.FIELD_LIST_STATUS_ACTIVE,
    inactive: LocalizedText.FIELD_LIST_STATUS_INACTIVE,
} as {
    [key in FieldListSearchResult['status']]: LocalizedText
}

const fieldListStatusColorMap = {
    active: 'green' as 'success',
    inactive: 'error',
} as {
    [key in FieldListSearchResult['status']]: ButtonProps['color']
}

export const FieldListsDataTableColumns = [
    {
        id: 'name',
        columnName: window.i18n.automationEngine.HEADER_NAME,
        minWidth: 150,
        renderCell: (node: FieldListSearchResult, nodeContext: FieldListsListBaseNodeContext) => (
            <Link role="button" data-id={`fieldList-${node.fieldListId}-name`} onClick={(event: any) => {
                event.preventDefault()
                nodeContext.viewFieldListSelected(node)
            }}>{node.name}</Link>
        )
    },
    {
        id: 'status',
        columnName: window.i18n.automationEngine.HEADER_STATUS,
        minWidth: 80,
        maxWidth: 90,
        renderCell: (node: FieldListSearchResult) => (
            <LocalizedTypography
                data-id={`fieldList-${node.fieldListId}-status`}
                color={fieldListStatusColorMap[node.status.toLowerCase() as FieldListStatusType]}
                text={fieldListStatusTranslationMap[node.status.toLowerCase() as FieldListStatusType]}
            />
        )
    },
    {
        id: 'lastUpdatedDate',
        columnName: window.i18n.automationEngine.HEADER_LASTUPDATED,
        minWidth: 150,
        maxWidth: 180,
        renderCell: (node: FieldListSearchResult) => (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
                <Typography data-id={`fieldList-${node.fieldListId}-lastUpdatedDate`}>{new Date(node.lastUpdatedDate).toLocaleDateString('en-US') }</Typography>
                <Typography data-id={`fieldList-${node.fieldListId}-lastUpdatedUserFullName`}>{window.i18n.automationEngine.FIELD_LIST_UPDATEDBY.replace(':name', node.lastUpdatedUserFullName)}</Typography>
            </div>
        ),
    },
]

export const FieldListsDataTableColumnNames = FieldListsDataTableColumns.map(column => column.columnName)

const FieldListsDataTableContainer = styled('div')(({ }: { theme: any }) => ({

}))

interface FieldListsDataTableProps {
    viewContextProps: any
    dataStatus: 'loading' | 'success' | 'error'
    totalCount: number
    onRefetch: () => void
    onFieldListSelected: (fieldList: FieldListSearchResult) => void
    viewFieldListSelected: (fieldList: FieldListSearchResult) => void
    changeFieldListStatus: (fieldList: FieldListSearchResult) => void
}

export const FieldListDataTable: FC<FieldListsDataTableProps> = ({
    viewContextProps,
    dataStatus,
    totalCount,
    onFieldListSelected,
    changeFieldListStatus,
}) => {
    const {
        FIELD_LIST_EDIT,
        FIELD_LIST_STATUS_MAKE,
        FIELD_LIST_STATUS_ACTIVE,
        FIELD_LIST_STATUS_INACTIVE,
    } = useMultipleLocalizedText()

    if (dataStatus === 'error') {
        return (
            <Paper>
                <Box display="flex" padding={2} alignItems="center" flexDirection="column" justifyContent="center">
                    <LocalizedTypography textAlign="center" color="error" text={LocalizedText.FIELD_LIST_ERROR_FETCH} />
                </Box>
            </Paper>
        )
    }

    if (totalCount === 0) {
        return (
            <Paper>
                <Box padding={2} alignItems="center">
                    <LocalizedTypography textAlign="center" text={LocalizedText.FIELD_LIST_NO_RESULTS} />
                </Box>
            </Paper>
        )
    }

    return (
        <FieldListsDataTableContainer>
            <DataTable
                {...viewContextProps}
                columns={FieldListsDataTableColumns}
                renderActions={(node: FieldListSearchResult) => (
                    <ActionGroup
                        id={`action-group=${node.fieldListId}`}
                        actions={[
                            {
                                label: FIELD_LIST_EDIT,
                                onClick: () => {
                                    onFieldListSelected(node)
                                }
                            },
                            {
                                label: FIELD_LIST_STATUS_MAKE.replace(':status',
                                    node.status !== 'ACTIVE' ?
                                        FIELD_LIST_STATUS_ACTIVE :
                                        FIELD_LIST_STATUS_INACTIVE
                                ),
                                onClick: () => {
                                    changeFieldListStatus(node)
                                }
                            }
                        ]}
                    />
                )}
            />
        </FieldListsDataTableContainer>
    )
}

export default FieldListDataTable
